import React, { useEffect, useState } from "react";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import navigationData from "../Datas/navigationData";
import { formatCurrency } from "../utils/format";
import { useCart } from "./CartContext";

const Header = () => {
  const { cartCount, wishlist } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.classList.toggle("menu-is-open", menuOpen);
    return () => document.body.classList.remove("menu-is-open");
  }, [menuOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmed = query.trim();
    navigate(trimmed ? `/shop?search=${encodeURIComponent(trimmed)}` : "/shop");
  };

  return (
    <>
      <div className="shipping-promo" role="status">
        <span>
          Flat-rate shipping{" "}
          {formatCurrency(siteConfig.standardShippingRate, siteConfig.currency)}
        </span>
        <div className="shipping-promo__track" aria-hidden="true">
          <span
            style={{
              width: "100%",
            }}
          />
        </div>
      </div>
      <header className="site-header">
        <div className="site-header__main shell">
          <button
            className="header-mobile-button"
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-controls="mobile-navigation-drawer"
            aria-expanded={menuOpen}
          >
            <FiMenu aria-hidden="true" />
          </button>

          <form
            className={`header-search ${searchOpen ? "is-open" : ""}`}
            role="search"
            onSubmit={handleSearch}
          >
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search our market"
              aria-label="Search products"
            />
          </form>

          <Link
            className="site-logo"
            to="/"
            aria-label={`${siteConfig.brandName} home`}
          >
            <img src={siteConfig.logo} alt={siteConfig.brandName} />
          </Link>

          <div className="header-actions">
            <button
              type="button"
              className="header-search-toggle"
              onClick={() => setSearchOpen((open) => !open)}
              aria-label="Toggle search"
            >
              <FiSearch aria-hidden="true" />
            </button>

            <Link
              to="/shop?wishlist=true"
              className="header-action header-action--counted"
              aria-label={`${wishlist.length} favorites`}
            >
              <FiHeart aria-hidden="true" />
              <span>Favorites</span>
              {wishlist.length > 0 && <b>{wishlist.length}</b>}
            </Link>
            <Link
              to="/cart"
              className="header-action header-action--counted"
              aria-label={`${cartCount} items in cart`}
            >
              <FiShoppingBag aria-hidden="true" />
              <span>Cart</span>
              {cartCount > 0 && <b>{cartCount}</b>}
            </Link>
          </div>
        </div>

        <nav className="desktop-navigation" aria-label="Primary navigation">
          <div className="shell desktop-navigation__inner">
            {navigationData.map((item) => (
              <div className="nav-group" key={item.category}>
                <NavLink
                  to={`/shop/${item.category.toLowerCase()}`}
                  className={({ isActive }) => (isActive ? "active" : "")}
                >
                  {item.label} <FiChevronDown aria-hidden="true" />
                </NavLink>
                <div className="mega-menu">
                  <div className="mega-menu__intro">
                    <span>Explore</span>
                    <h3>{item.label}</h3>
                    <Link to={`/shop/${item.category.toLowerCase()}`}>
                      Shop all
                    </Link>
                  </div>
                  {item.columns.map((column) => (
                    <div key={column.title}>
                      <h4>{column.title}</h4>
                      {column.links.map((link) => (
                        <Link
                          key={link}
                          to={`/shop/${item.category.toLowerCase()}?filter=${encodeURIComponent(
                            link
                          )}`}
                        >
                          {link}
                        </Link>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <NavLink to="/shop" className="nav-sale">
              Sale
            </NavLink>
            <div className="nav-group nav-group--more">
              <button type="button">
                More <FiChevronDown aria-hidden="true" />
              </button>
              <div className="mega-menu mega-menu--compact">
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/cookies">Cookie Policy</Link>
                <Link to="/terms">Terms &amp; Conditions</Link>
                <Link to="/faqs">FAQs</Link>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <div
        className={`mobile-drawer-backdrop ${menuOpen ? "is-visible" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <aside
        id="mobile-navigation-drawer"
        className={`mobile-drawer ${menuOpen ? "is-open" : ""}`}
        aria-hidden={!menuOpen}
      >
        <div className="mobile-drawer__header">
          <img src={siteConfig.logo} alt={siteConfig.brandName} />
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            aria-label="Close menu"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        <form className="mobile-drawer__search" onSubmit={handleSearch}>
          <FiSearch aria-hidden="true" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search our market"
            aria-label="Search products"
          />
        </form>
        <nav aria-label="Mobile navigation">
          <Link to="/shop">Shop All</Link>
          {navigationData.map((item) => (
            <Link
              key={item.category}
              to={`/shop/${item.category.toLowerCase()}`}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/shop?sale=true">Sale</Link>
        </nav>
        <div className="mobile-drawer__links">
          <Link to="/about">Our Story</Link>
          <Link to="/shipping">Shipping</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/cookies">Cookie Policy</Link>
          <Link to="/faqs">FAQs</Link>
        </div>
      </aside>
    </>
  );
};

export default Header;
